import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  Box,
  styled,
  tableCellClasses,
  TablePagination,
} from "@mui/material";
import { apiService } from "../../../api/apiService";
import { getStatusStyles } from '../../../components/Reusable/utils/getStatusStyles';
import VisionNStrategyModalAdmin from "../Popup/VisionNStrategyModalAdmin";

interface CustomTableAdminProps {
  management: string;
  section_name: string;
  data: any[];
  onRefresh?: () => void; // Add onRefresh prop
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    border: "none",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: '#000',
    border: "none",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#f5f5f5",
    cursor: "pointer",
  },
}));

const CustomTableAdmin: React.FC<CustomTableAdminProps> = ({ 
  management, 
  section_name, 
  data, // Use data from props
  onRefresh 
}) => {
  const { instance } = useMsal();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [modalData, setModalData] = useState<any | null>(null);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<{ 
    id: any, 
    function_l1: any, 
    created_at: any, 
    status: any, 
    reason_to_approve: any, 
    reason_to_revert: any, 
    created_by: any 
  } | null>(null);

  const handleViewClick = (
    id: any, 
    file_name: string, 
    function_l1: any, 
    created_at: any, 
    status: any, 
    reason_to_approve: any, 
    reason_to_revert: any, 
    created_by: any
  ) => {
    setPopupOpen(true);
    setSelectedFile({ 
      id, 
      function_l1, 
      created_at, 
      status, 
      reason_to_approve, 
      reason_to_revert, 
      created_by 
    });
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    if (onRefresh) onRefresh(); // Refresh data when popup closes
  };

  const handleRowClick = async (
    rowId: number, 
    file_name: string, 
    function_l1: any, 
    created_at: any, 
    status: any, 
    reason_to_approve: any, 
    reason_to_revert: any,
    created_by: any
  ) => {
    setSelectedRowId(rowId);
    setIsLoading(true);
    try {
      const response = await apiService(`/strategy/vision_n_strategy/${rowId}`, "GET", undefined, undefined, instance as PublicClientApplication);
      if (response && response.data) {
        setModalData(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch details:", error);
    } finally {
      setIsLoading(false);
    }
    handleViewClick(
      rowId, 
      file_name, 
      function_l1, 
      created_at, 
      status, 
      reason_to_approve, 
      reason_to_revert,
      created_by
    );
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const columns = [
    "KPI name",
    "UOM",
    "Current Status",
    "Target 2025",
    "North Year",
    "North Year Value",
    "Modified by",
    "Modified on",
    "Status",
  ];

  return (
    <>
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 'none' }}>
          <TableContainer sx={{ maxHeight: 400, minHeight: 200 }}>
            <Table stickyHeader aria-label="customized table">
              <TableHead>
                <TableRow>
                  {columns.map((column, index) => (
                    <StyledTableCell key={index} align="center">
                      {column}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, rowIndex) => {
                    const statusStyles = getStatusStyles(row.status);

                    return (
                      <StyledTableRow 
                        key={rowIndex} 
                        hover 
                        onClick={() => handleRowClick(
                          row.id, 
                          row.file_name, 
                          row.function_l1, 
                          row.created_at, 
                          row.status, 
                          row.reason_to_approve, 
                          row.reason_to_revert,
                          row.created_by
                        )}
                      >
                        <StyledTableCell align="center">{row.KPI_name}</StyledTableCell>
                        <StyledTableCell align="center">{row.UoM}</StyledTableCell>
                        <StyledTableCell align="center">{row.currentstatus}</StyledTableCell>
                        <StyledTableCell align="center">
                          {row.target_year === 2025 ? row.target_year_value : "-"}
                        </StyledTableCell>
                        <StyledTableCell align="center">{row.north_star}</StyledTableCell>
                        <StyledTableCell align="center">{row.north_star_value}</StyledTableCell>
                        <StyledTableCell align="center">{row.created_by}</StyledTableCell>
                        <StyledTableCell align="center">
                          {new Date(row.created_at).toLocaleDateString()}
                        </StyledTableCell>
                        <StyledTableCell align="center" sx={{ color: statusStyles.color }}>
                          {row.status}
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "350px",
                      }}>
                        <Typography sx={{ fontWeight: "bold" }}>
                          There's no file uploaded right now
                        </Typography>
                        <Typography>Add BSC KPIs contributing to the GBS Mission KPIs</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-selectIcon': {
                color: '#000',
              },
              '& .MuiMenuItem-root': {
                color: '#000',
              },
            }}
          />
        </Paper>
      )}

      {selectedFile && (
        <VisionNStrategyModalAdmin
          onClose={handleClosePopup}
          open={isPopupOpen}
          id={selectedFile.id}
          functionl1={selectedFile.function_l1}
          date={selectedFile.created_at}
          status={selectedFile.status}
          reason_to_approve={selectedFile.reason_to_approve}
          reason_to_revert={selectedFile.reason_to_revert}
          sectionTitle=''
          statusApiPath="/strategy/vision_n_strategy_status"
          data={modalData}
        />
      )}
    </>
  );
};

export default CustomTableAdmin;